import { useState, FormEvent } from 'react'
import { MenuItem, MenuFormData } from '../Menus'
import Button from '../../../components/common/Button'

interface MenuFormProps {
  initialData?: MenuItem
  onSubmit: (data: MenuFormData) => Promise<void>
  onCancel: () => void
}

function MenuForm({ initialData, onSubmit, onCancel }: MenuFormProps) {
  const [formData, setFormData] = useState<MenuFormData>({
    label: initialData?.label || '',
    is_visible: initialData?.is_visible ?? true,
  })

  const [errors, setErrors] = useState<Partial<Record<keyof MenuFormData, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof MenuFormData, string>> = {}

    if (!formData.label.trim()) {
      newErrors.label = 'Le libellé est requis'
    } else if (formData.label.length > 50) {
      newErrors.label = 'Le libellé ne peut pas dépasser 50 caractères'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setSubmitting(true)
      setSubmitError('')
      await onSubmit(formData)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="menu-form">
      {submitError && (
        <div className="error-message">
          <span className="error-icon">⚠</span>
          {submitError}
        </div>
      )}

      <div className="form-field">
        <label htmlFor="label">
          Libellé <span className="required">*</span>
        </label>
        <input
          id="label"
          type="text"
          value={formData.label}
          onChange={(e) => setFormData({ ...formData, label: e.target.value })}
          className={errors.label ? 'error' : ''}
          placeholder="Ex: Accueil, L'équipe, Actualités..."
          maxLength={50}
        />
        {errors.label && <span className="field-error">{errors.label}</span>}
      </div>

      {initialData && (
        <div className="form-field">
          <label>Slug (non modifiable)</label>
          <input
            type="text"
            value={initialData.slug}
            disabled
            className="disabled-input"
          />
          <small className="field-help">
            Le slug est l'identifiant technique du menu et ne peut pas être modifié
          </small>
        </div>
      )}

      <div className="form-field checkbox-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.is_visible}
            onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
          />
          <span>Menu visible sur le site</span>
        </label>
        <small className="field-help">
          Si décoché, le menu ne sera pas affiché dans la navigation du site
        </small>
      </div>

      <div className="modal-footer">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={submitting}
        >
          {submitting ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  )
}

export default MenuForm
